import React, { useState } from "react";
import { fileUrl, geocodeIntervention, setInterventionLocation, generateReport, sendReport } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { LocateFixed, RefreshCw, Save, FileText, Send, Download, Loader2 } from "lucide-react";

export const LocationEditor = ({ it, onReload }) => {
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState(it.site_gps?.lat ?? "");
  const [lng, setLng] = useState(it.site_gps?.lng ?? "");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const inp = "border border-neutral-300 rounded-md px-2.5 py-1.5 text-sm w-32 focus:outline-none focus:border-brand";

  const reGeocode = async () => {
    setBusy(true);
    try {
      const res = await geocodeIntervention(it.id);
      if (res.data.site_gps) {
        setLat(res.data.site_gps.lat); setLng(res.data.site_gps.lng);
        toast({ title: "Adresse localisée ✓" });
        onReload();
      } else {
        toast({ title: "Adresse introuvable", description: "Saisissez la position manuellement.", variant: "destructive" });
      }
    } finally { setBusy(false); }
  };

  const saveManual = async () => {
    if (lat === "" || lng === "") { toast({ title: "Latitude et longitude requises", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await setInterventionLocation(it.id, parseFloat(lat), parseFloat(lng));
      toast({ title: "Position enregistrée ✓" });
      setOpen(false);
      onReload();
    } finally { setBusy(false); }
  };

  const located = !!it.site_gps?.lat;

  return (
    <div className="mt-3" data-testid={`intv-location-${it.id}`}>
      <button onClick={() => setOpen(!open)} className={`text-sm flex items-center gap-1.5 ${located ? "text-neutral-500 hover:text-brand" : "text-amber-600"}`} data-testid={`intv-location-toggle-${it.id}`}>
        <LocateFixed size={14} />
        {located ? <>Site localisé ✓ <span className="text-neutral-400">· ajuster</span></> : "Adresse non localisée — définir la position"}
      </button>
      {open && (
        <div className="mt-2 bg-neutral-50 border border-neutral-200 rounded-lg p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Latitude</label>
              <input className={inp} value={lat} onChange={(e) => setLat(e.target.value)} placeholder="50.6293" data-testid={`intv-lat-${it.id}`} />
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Longitude</label>
              <input className={inp} value={lng} onChange={(e) => setLng(e.target.value)} placeholder="3.0573" data-testid={`intv-lng-${it.id}`} />
            </div>
            <button onClick={saveManual} disabled={busy} className="flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-3 py-1.5 rounded-md disabled:opacity-60" data-testid={`intv-savepos-${it.id}`}>
              <Save size={14} /> Enregistrer
            </button>
            <button onClick={reGeocode} disabled={busy} className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-brand px-2 py-1.5" data-testid={`intv-regeocode-${it.id}`}>
              <RefreshCw size={14} /> Depuis l'adresse
            </button>
          </div>
          <p className="text-xs text-neutral-400 mt-2">Astuce : sur Google Maps / OpenStreetMap, faites un clic droit sur le lieu pour copier les coordonnées.</p>
        </div>
      )}
    </div>
  );
};

export const ReportActions = ({ it, onReload }) => {
  const [busy, setBusy] = useState(null); // 'gen' | 'send'
  const [reportPath, setReportPath] = useState(it.report_path || "");
  const { toast } = useToast();

  const gen = async () => {
    setBusy("gen");
    try {
      const res = await generateReport(it.id);
      setReportPath(res.data.report_path);
      toast({ title: "Rapport PDF généré ✓" });
      onReload();
    } catch (e) {
      toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec de génération", variant: "destructive" });
    } finally { setBusy(null); }
  };

  const send = async () => {
    const to = it.client_email || window.prompt("Adresse email du client :", "");
    if (!to) return;
    setBusy("send");
    try {
      const res = await sendReport(it.id, to);
      if (res.data.ok) {
        setReportPath(res.data.report_path);
        toast({ title: "Rapport envoyé au client ✓", description: to });
        onReload();
      } else {
        toast({ title: "Échec de l'envoi", description: res.data.error || "Erreur SMTP", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec de l'envoi", variant: "destructive" });
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap items-center gap-3" data-testid={`intv-report-${it.id}`}>
      <FileText size={15} className="text-brand" />
      {reportPath ? (
        <a href={fileUrl(reportPath)} target="_blank" rel="noreferrer" className="text-sm text-brand hover:underline flex items-center gap-1.5" data-testid={`intv-report-download-${it.id}`}>
          <Download size={14} /> Télécharger le rapport PDF
        </a>
      ) : (
        <span className="text-sm text-neutral-500">Rapport PDF non généré</span>
      )}
      <button onClick={gen} disabled={busy} className="text-sm text-neutral-600 hover:text-brand flex items-center gap-1.5 disabled:opacity-60" data-testid={`intv-report-generate-${it.id}`}>
        {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {reportPath ? "Régénérer" : "Générer le rapport"}
      </button>
      <button onClick={send} disabled={busy} className="ml-auto flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-3 py-1.5 rounded-md disabled:opacity-60" data-testid={`intv-report-send-${it.id}`}>
        {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer au client
      </button>
    </div>
  );
};
